import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import './AdminDashboard.css';

const AdminDpPurchaseHistory = () => {
  const navigate = useNavigate();
  const [dpPurchases, setDpPurchases] = useState([]);
  const [userPseudos, setUserPseudos] = useState({});
  const [selectedPurchases, setSelectedPurchases] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  // Load DP purchases and user pseudos
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load DP purchases
        const transactionsQuery = query(
          collection(db, 'transactions'),
          orderBy('timestamp', 'desc')
        );
        const transactionsSnapshot = await getDocs(transactionsQuery);
        const transactions = transactionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDpPurchases(transactions);

        // Load user pseudos for all unique user IDs
        const uniqueUserIds = [...new Set(transactions.map(t => t.userId).filter(Boolean))];
        const pseudoMap = {};
        
        for (const userId of uniqueUserIds) {
          try {
            const userDoc = await getDocs(collection(db, 'users'));
            const userDocs = userDoc.docs;
            const user = userDocs.find(doc => doc.id === userId);
            if (user) {
              const userData = user.data();
              pseudoMap[userId] = userData.pseudo || 'Unknown';
            }
          } catch (error) {
            console.error(`Error loading pseudo for user ${userId}:`, error);
            pseudoMap[userId] = 'Unknown';
          }
        }
        
        setUserPseudos(pseudoMap);
      } catch (error) {
        console.error('Error loading DP purchases:', error);
        setDpPurchases([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle individual checkbox change
  const handlePurchaseSelect = (purchaseId) => {
    const newSelected = new Set(selectedPurchases);
    if (newSelected.has(purchaseId)) {
      newSelected.delete(purchaseId);
    } else {
      newSelected.add(purchaseId);
    }
    setSelectedPurchases(newSelected);
    setIsAllSelected(newSelected.size === dpPurchases.length && dpPurchases.length > 0);
  };

  // Handle global checkbox change
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedPurchases(new Set());
      setIsAllSelected(false);
    } else {
      const allIds = new Set(dpPurchases.map(p => p.id));
      setSelectedPurchases(allIds);
      setIsAllSelected(true);
    }
  };

  // Delete single purchase
  const handleDeleteSingle = async (purchaseId) => {
    if (!window.confirm("Are you sure you want to delete this DP purchase record?")) return;
    
    try {
      setDeleting(true);
      await deleteDoc(doc(db, 'transactions', purchaseId));
      
      // Update local state
      setDpPurchases(prev => prev.filter(p => p.id !== purchaseId));
      setSelectedPurchases(prev => {
        const newSet = new Set(prev);
        newSet.delete(purchaseId);
        return newSet;
      });
      
      alert("✅ DP purchase record deleted successfully!");
    } catch (error) {
      console.error('Error deleting purchase:', error);
      alert("❌ Failed to delete DP purchase record.");
    } finally {
      setDeleting(false);
    }
  };

  // Delete selected purchases
  const handleDeleteSelected = async () => {
    if (selectedPurchases.size === 0) {
      alert("Please select at least one DP purchase record to delete.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedPurchases.size} DP purchase record(s)?`)) return;
    
    try {
      setDeleting(true);
      
      // Delete all selected purchases
      const deletePromises = Array.from(selectedPurchases).map(purchaseId => 
        deleteDoc(doc(db, 'transactions', purchaseId))
      );
      
      await Promise.all(deletePromises);
      
      // Update local state
      setDpPurchases(prev => prev.filter(p => !selectedPurchases.has(p.id)));
      setSelectedPurchases(new Set());
      setIsAllSelected(false);
      
      alert(`✅ ${selectedPurchases.size} DP purchase record(s) deleted successfully!`);
    } catch (error) {
      console.error('Error deleting purchases:', error);
      alert("❌ Failed to delete DP purchase records.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <h2>💰 DP Purchase History</h2>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading DP purchase history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <h2>💰 DP Purchase History</h2>
      
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center' }}>
        <button onClick={() => navigate('/admin-panelx-952504')}>⬅️ Back to Dashboard</button>
        <button onClick={() => navigate('/admin-shop-manager')}>🛒 Shop Manager</button>
        
        {selectedPurchases.size > 0 && (
          <button 
            onClick={handleDeleteSelected}
            disabled={deleting}
            style={{ 
              backgroundColor: '#f44336', 
              color: 'white',
              opacity: deleting ? 0.6 : 1
            }}
          >
            {deleting ? 'Deleting...' : `🗑️ Delete Selected (${selectedPurchases.size})`}
          </button>
        )}
      </div>

      {dpPurchases.length > 0 ? (
        <div style={{ border: '1px solid #ddd', borderRadius: '5px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
              <tr>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    style={{ transform: 'scale(1.2)' }}
                  />
                </th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>User ID</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Pseudo</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Amount</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Method</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dpPurchases.map((purchase) => (
                <tr 
                  key={purchase.id} 
                  style={{ 
                    backgroundColor: purchase.status === 'Completed' ? '#e8f5e8' : '#fff3cd',
                    opacity: selectedPurchases.has(purchase.id) ? 0.7 : 1
                  }}
                >
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedPurchases.has(purchase.id)}
                      onChange={() => handlePurchaseSelect(purchase.id)}
                      style={{ transform: 'scale(1.1)' }}
                    />
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontSize: '12px', fontFamily: 'monospace' }}>
                    {purchase.userId ? purchase.userId.substring(0, 8) + '...' : 'N/A'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                    {userPseudos[purchase.userId] || 'Loading...'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', color: '#2e7d32' }}>
                    +{purchase.amount || purchase.dpAmount || 0} DP
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {purchase.method || 'Unknown'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      backgroundColor: purchase.status === 'Completed' ? '#4caf50' : '#ff9800',
                      color: 'white'
                    }}>
                      {purchase.status || 'Pending'}
                    </span>
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontSize: '12px' }}>
                    {purchase.timestamp?.toDate?.()?.toLocaleDateString() || 'N/A'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontSize: '12px' }}>
                    {purchase.description || 'DP Purchase'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    <button
                      onClick={() => handleDeleteSingle(purchase.id)}
                      disabled={deleting}
                      style={{
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: deleting ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        opacity: deleting ? 0.6 : 1
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <h3>No DP purchases found</h3>
          <p>There are no DP purchase records in the system.</p>
        </div>
      )}

      {dpPurchases.length > 0 && (
        <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '5px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            <strong>Total Records:</strong> {dpPurchases.length} | 
            <strong> Selected:</strong> {selectedPurchases.size} | 
            <strong> Completed:</strong> {dpPurchases.filter(p => p.status === 'Completed').length} | 
            <strong> Pending:</strong> {dpPurchases.filter(p => p.status !== 'Completed').length}
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminDpPurchaseHistory;
